/**
 * Simplified API Client
 * 
 * Single file containing all API calls with minimal abstraction.
 * Uses direct fetch() with simple error handling.
 */

import type { 
  ApiResponse, 
  PaginatedData, 
  User, 
  AuthResponse, 
  LoginRequest, 
  App, 
  Company,
  Conversation, 
  Message, 
  Customer, 
  CustomerMergeRequest,
  CustomerDuplicatesResponse,
  Queue,
  ConversationFilters,
  CustomerFilters,
  DashboardData,
  ConversationPollResponse,
  ReportingOverview,
  ReportingCharts,
  ReportingAgentPerformance,
  PlatformCompany,
  PlatformUser,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  Activity,
  ApiKey,
  ApiKeyListResponse,
  CreateApiKeyRequest,
  UpdateApiKeyRequest,
  Invoice,
  InvoiceDetail,
  InvoiceStatistics,
  CurrentUsage,
  UsageHistory,
  PaymentMethodInfo,
  PortalSessionResponse,
  StripeCustomerInfo,
  CreateStripeCustomerResponse
} from './types'

// Configuration
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://local.messagelink.cloud/api/v1'

// Global state for auth and company context
let authToken: string | null = null
let companyId: string | null = null
let selectedAppId: string | null = null
let impersonateUserId: string | null = null

// === UTILITIES ===

function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
  
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`
  }
  
  if (companyId) {
    headers['X-Company-ID'] = companyId
  }
  
  if (impersonateUserId) {
    headers['X-Impersonate-User-ID'] = impersonateUserId
  }
  
  return headers
}

export async function apiCall<T>(
  endpoint: string, 
  options: RequestInit & { skipCompanyHeader?: boolean } = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  
  const headers = getHeaders()
  
  // Skip company header for admin endpoints
  if (options.skipCompanyHeader) {
    delete headers['X-Company-ID']
  }
  
  const { skipCompanyHeader, ...fetchOptions } = options
  
  const response = await fetch(url, {
    ...fetchOptions,
    headers: {
      ...headers,
      ...fetchOptions.headers,
    },
  })
  
  if (!response.ok) {
    // Handle auth errors
    if (response.status === 401) {
      setAuthToken(null)
      if (typeof window !== 'undefined' && !endpoint.includes('/auth/login')) {
        // Save the current location for redirect after login
        const currentPath = window.location.pathname + window.location.search + window.location.hash
        const redirectParam = encodeURIComponent(currentPath)
        window.location.href = `/login?redirect=${redirectParam}`
      }
    }
    
    let errorData: any = {}
    try {
      errorData = await response.json()
    } catch (e) {
      // If response is not JSON, create a generic error
      errorData = { error: { message: `HTTP ${response.status}: ${response.statusText}` } }
    }
    
    // Extract error message from various possible response formats
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`
    
    if (errorData.error?.message) {
      errorMessage = errorData.error.message
    } else if (errorData.message) {
      errorMessage = errorData.message
    } else if (typeof errorData.error === 'string') {
      errorMessage = errorData.error
    } else if (errorData.errors && Object.keys(errorData.errors).length > 0) {
      // Handle validation errors
      const firstError = Object.values(errorData.errors)[0]
      errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
    }
    
    const error = new Error(errorMessage)
    ;(error as any).status = response.status
    ;(error as any).errors = errorData.errors || errorData.error?.validation_errors || {}
    ;(error as any).originalResponse = errorData
    throw error
  }
  
  return response.json()
}

function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value))
    }
  })
  
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

// === TOKEN MANAGEMENT ===

export function setAuthToken(token: string | null) {
  authToken = token
  if (typeof window !== 'undefined') {
    if (token) {
      localStorage.setItem('auth_token', token)
    } else {
      localStorage.removeItem('auth_token')
    }
  }
}

export function loadAuthToken() {
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('auth_token')
    if (token) {
      authToken = token
    }
  }
}

export function getAuthToken(): string | null {
  return authToken
}

export function setCompanyId(id: string | null) {
  companyId = id
  if (typeof window !== 'undefined') {
    if (id) {
      localStorage.setItem('company_id', id)
    } else {
      localStorage.removeItem('company_id')
    }
  }
}

export function loadCompanyId() {
  if (typeof window !== 'undefined') {
    const id = localStorage.getItem('company_id')
    if (id) {
      companyId = id
    }
  }
}

export function setSelectedAppId(id: string | null) {
  selectedAppId = id
  if (typeof window !== 'undefined') {
    if (id) {
      localStorage.setItem('selected_app_id', id)
    } else {
      localStorage.removeItem('selected_app_id')
    }
  }
}

export function loadSelectedAppId() {
  if (typeof window !== 'undefined') {
    const id = localStorage.getItem('selected_app_id')
    if (id) {
      selectedAppId = id
    }
  }
}

export function getSelectedAppId(): string | null {
  return selectedAppId
}

export function setImpersonateUserId(userId: string | null) {
  impersonateUserId = userId
}

// === AUTH API ===

export async function login(data: LoginRequest): Promise<AuthResponse> {
  const response = await apiCall<ApiResponse<AuthResponse>>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  
  const authData = response.data
  
  setAuthToken(authData.access_token)
  
  if (authData.current_company) {
    setCompanyId(authData.current_company.company_id)
  }
  
  return authData
}

export async function getCurrentUser(): Promise<User> {
  const response = await apiCall<ApiResponse<User>>('/auth/me')
  return response.data
}

export async function updateUserProfile(data: { 
  name?: string; 
  email?: string;
  avatar?: string;
  initials?: string;
  timezone?: string;
}): Promise<User> {
  const response = await apiCall<ApiResponse<User>>('/auth/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  return response.data
}

export async function logout(): Promise<void> {
  await apiCall('/auth/logout', { method: 'POST' })
  setAuthToken(null)
  setCompanyId(null)
  setImpersonateUserId(null)
}

export async function requestPasswordReset(email: string): Promise<{ code: string; message: string }> {
  const response = await apiCall<{ success: boolean; data: { message: string } }>('/auth/password_reset', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
  
  // Extract the message from the data field and return in expected format
  return {
    code: 'success',
    message: response.data?.message || 'If the email address exists, a password reset link has been sent'
  }
}

export async function resetPasswordWithHash(hash: string, password: string): Promise<{ code: string; message: string }> {
  return apiCall<{ code: string; message: string }>('/auth/password_reset_hash', {
    method: 'POST',
    body: JSON.stringify({ hash, password }),
  })
}

// === COMPANIES API ===

export async function getCompanies(params: { page?: number; limit?: number; search?: string } = {}): Promise<PaginatedData<Company>> {
  const queryString = buildQueryString(params)
  return apiCall<PaginatedData<Company>>(`/companies${queryString}`)
}

export async function getCompany(companyId: string): Promise<Company> {
  const response = await apiCall<ApiResponse<Company>>(`/companies?id=${companyId}`)
  return response.data
}

export async function createCompany(data: { company_name: string; domain?: string; timezone: string }): Promise<Company> {
  const response = await apiCall<ApiResponse<Company>>('/companies', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  return response.data
}

export async function updateCompany(companyId: string, data: Partial<Company>): Promise<Company> {
  const response = await apiCall<ApiResponse<Company>>(`/companies?id=${companyId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  return response.data
}

// === APPS API ===

export async function getApps(params: { page?: number; limit?: number; status?: string } = {}): Promise<PaginatedData<App>> {
  const queryString = buildQueryString(params)
  return apiCall<PaginatedData<App>>(`/apps${queryString}`)
}

export async function getApp(appId: string): Promise<App> {
  const response = await apiCall<ApiResponse<App>>(`/apps?id=${appId}`)
  return response.data
}

export async function createApp(data: { app_name: string; description?: string; logo_url?: string }): Promise<App> {
  const response = await apiCall<ApiResponse<App>>('/apps', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  return response.data
}

export async function updateApp(appId: string, data: Partial<App>): Promise<App> {
  const response = await apiCall<ApiResponse<App>>(`/apps?id=${appId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  return response.data
}

export async function deleteApp(appId: string): Promise<void> {
  await apiCall(`/apps?id=${appId}`, {
    method: 'DELETE',
  })
}

export interface AppStats {
  active_users: number
  active_users_delta: number
  total_users: number
  active_queues: number
  active_queues_delta: number
  total_queues: number
  conversations_today: number
  conversations_today_delta: number
  conversations_today_delta_percent: number
  conversations_week: number
  conversations_month: number
  avg_response_time: number
  avg_response_time_delta: number
  avg_resolution_time: number
  avg_resolution_time_delta: number
  total_integrations?: number
  active_integrations?: number
  integrations?: Array<{
    integration_id: string
    integration_type: string
    integration_name: string
    status: string
    last_activity_ts?: string
  }>
  last_activity_ts?: string
}

export async function getAppStats(appId: string): Promise<AppStats> {
  const response = await apiCall<ApiResponse<AppStats>>(`/app/stats?id=${appId}`)
  return response.data
}

// === CONVERSATIONS API ===

export async function getConversations(filters: ConversationFilters = {}): Promise<PaginatedData<Conversation>> {
  const queryString = buildQueryString(filters)
  return apiCall<PaginatedData<Conversation>>(`/conversations${queryString}`)
}

export async function pollConversations(params: { 
  app_id?: string
  queue_id?: string
  queue_ids?: string
  conversation_id?: string
  last_message_id?: string
  limit?: number
  resolved?: string
} = {}): Promise<ConversationPollResponse> {
  const queryString = buildQueryString(params)
  const response = await apiCall<ApiResponse<ConversationPollResponse>>(`/conversation/poll${queryString}`)
  return response.data
}

export async function getDashboardConversations(params: {
  app_id?: string
  limit?: number
} = {}): Promise<DashboardData> {
  const queryString = buildQueryString(params)
  const response = await apiCall<ApiResponse<DashboardData>>(`/dashboards${queryString}`)
  return response.data
}

export async function getConversation(conversationId: string): Promise<Conversation> {
  const response = await apiCall<ApiResponse<Conversation>>(`/conversations?id=${conversationId}`)
  return response.data
}

export async function getMessages(conversationId: string, params: { page?: number; limit?: number } = {}): Promise<PaginatedData<Message>> {
  const queryString = buildQueryString({ conversation_id: conversationId, ...params })
  return apiCall<PaginatedData<Message>>(`/messages${queryString}`)
}

export async function sendMessage(conversationId: string, content: string): Promise<Message> {
  const response = await apiCall<ApiResponse<Message>>('/messages', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: conversationId,
      content,
      content_type: 'text',
    }),
  })
  return response.data
}

export async function setTypingIndicator(conversationId: string): Promise<void> {
  await apiCall('/conversation/typing', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: conversationId
    }),
  })
}

export async function flagConversation(conversationId: string, flagged: boolean): Promise<void> {
  await apiCall(`/conversation/flag`, {
    method: 'POST',
    body: JSON.stringify({ 
      conversation_id: conversationId,
      flagged 
    }),
  })
}

export async function markConversationAsRead(conversationId: string): Promise<void> {
  await apiCall(`/conversation/read`, {
    method: 'POST',
    body: JSON.stringify({ 
      conversation_id: conversationId
    }),
  })
}

export async function assignConversation(conversationId: string, userId: string): Promise<void> {
  await apiCall(`/conversation/assign`, {
    method: 'POST',
    body: JSON.stringify({ 
      conversation_id: conversationId,
      user_id: userId 
    }),
  })
}

export async function unassignConversation(conversationId: string): Promise<void> {
  await apiCall(`/conversation/unassign`, {
    method: 'POST',
    body: JSON.stringify({ 
      conversation_id: conversationId
    }),
  })
}

export async function transferConversation(
  conversationId: string, 
  queueId: string, 
  userId?: string,
  transferReason?: string
): Promise<void> {
  await apiCall(`/conversation/transfer`, {
    method: 'POST',
    body: JSON.stringify({ 
      conversation_id: conversationId,
      queue_id: queueId,
      user_id: userId,
      transfer_reason: transferReason
    }),
  })
}

export async function resolveConversation(conversationId: string): Promise<void> {
  await apiCall(`/conversation/resolve`, {
    method: 'POST',
    body: JSON.stringify({ 
      conversation_id: conversationId
    }),
  })
}

export async function deleteConversation(conversationId: string): Promise<void> {
  await apiCall(`/conversations?id=${conversationId}`, {
    method: 'DELETE',
  })
}

export async function generateConversationSummary(conversationId: string): Promise<{ summary: string }> {
  const response = await apiCall<ApiResponse<{ summary: string }>>(`/conversation/ai-summary`, {
    method: 'POST',
    body: JSON.stringify({ 
      conversation_id: conversationId
    }),
  })
  return response.data
}

export async function generateAICompletion(conversationId: string): Promise<{ completion: string; conversation_id: string }> {
  const response = await apiCall<ApiResponse<{ completion: string; conversation_id: string }>>(`/conversation/ai-completion`, {
    method: 'POST',
    body: JSON.stringify({ 
      conversation_id: conversationId
    }),
  })
  return response.data
}

export async function sendSecureConversationLink(conversationId: string, expiresInHours?: number): Promise<{ secure_link: string; expires_at: string; message_sent: boolean; warning?: string }> {
  const response = await apiCall<ApiResponse<{ secure_link: string; expires_at: string; message_sent: boolean; warning?: string }>>(`/conversation/secure-link`, {
    method: 'POST',
    body: JSON.stringify({ 
      conversation_id: conversationId,
      ...(expiresInHours && { expires_in_hours: expiresInHours })
    }),
  })
  return response.data
}

// === CUSTOMERS API ===

export async function getCustomers(filters: CustomerFilters = {}): Promise<PaginatedData<Customer>> {
  const queryString = buildQueryString(filters)
  return apiCall<PaginatedData<Customer>>(`/customers${queryString}`)
}

export async function getCustomer(customerId: string): Promise<Customer> {
  const response = await apiCall<ApiResponse<Customer>>(`/customers?id=${customerId}`)
  return response.data
}

export async function createCustomer(data: {
  name: string
  primary_email?: string
  primary_phone?: string
  metadata?: Record<string, any>
}): Promise<Customer> {
  const response = await apiCall<ApiResponse<Customer>>('/customers', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  return response.data
}

export async function updateCustomer(customerId: string, data: Partial<{
  name: string
  primary_email?: string
  primary_phone?: string
  metadata?: Record<string, any>
}>): Promise<Customer> {
  const response = await apiCall<ApiResponse<Customer>>(`/customers?id=${customerId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  return response.data
}

export async function deleteCustomer(customerId: string): Promise<void> {
  await apiCall(`/customers?id=${customerId}`, {
    method: 'DELETE',
  })
}

export async function getCustomerConversations(customerId: string, params: {
  page?: number
  limit?: number
  status?: string
  app_id?: string
  include_archived?: boolean
} = {}): Promise<PaginatedData<Conversation>> {
  const queryString = buildQueryString({ id: customerId, ...params })
  return apiCall<PaginatedData<Conversation>>(`/customer/conversations${queryString}`)
}

export async function getCustomerApps(customerId: string): Promise<Array<{
  app_id: string
  app_name: string
  description?: string
  logo_url?: string
  app_status: string
  conversations_count: number
  active_conversations_count: number
  assigned_ts: string
}>> {
  const response = await apiCall<ApiResponse<Array<{
    app_id: string
    app_name: string
    description?: string
    logo_url?: string
    app_status: string
    conversations_count: number
    active_conversations_count: number
    assigned_ts: string
  }>>>(`/customer/apps?id=${customerId}`)
  return response.data
}

export async function addCustomerToApp(customerId: string, appId: string): Promise<{
  customer_id: string
  name: string
  app_id: string
  app_name: string
  message: string
}> {
  const response = await apiCall<ApiResponse<{
    customer_id: string
    name: string
    app_id: string
    app_name: string
    message: string
  }>>('/customer/apps', {
    method: 'POST',
    body: JSON.stringify({
      customer_id: customerId,
      app_id: appId
    }),
  })
  return response.data
}

export async function removeCustomerFromApp(customerId: string, appId: string): Promise<void> {
  await apiCall(`/customer/apps?id=${customerId}&app_id=${appId}`, {
    method: 'DELETE',
  })
}

export async function getCustomerDuplicates(customerId: string): Promise<CustomerDuplicatesResponse> {
  const response = await apiCall<ApiResponse<CustomerDuplicatesResponse>>(`/customer/duplicates?customer_id=${customerId}`)
  return response.data
}

export async function mergeCustomers(data: CustomerMergeRequest): Promise<{
  primary_customer: {
    customer_id: string
    name: string
    primary_email?: string
    primary_phone?: string
  }
  secondary_customer: {
    customer_id: string
    name: string
    primary_email?: string
    primary_phone?: string
  }
  merge_stats: {
    conversations_moved: number
    apps_transferred: number
    confidence_score: number
  }
  link_id: string
  message: string
}> {
  const response = await apiCall<ApiResponse<{
    primary_customer: {
      customer_id: string
      name: string
      primary_email?: string
      primary_phone?: string
    }
    secondary_customer: {
      customer_id: string
      name: string
      primary_email?: string
      primary_phone?: string
    }
    merge_stats: {
      conversations_moved: number
      apps_transferred: number
      confidence_score: number
    }
    link_id: string
    message: string
  }>>('/customer/merge', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  return response.data
}

// === USERS API ===

export async function getUsers(params: { 
  page?: number; 
  limit?: number; 
  search?: string;
  app_id?: string;
  role?: string;
  status?: string;
} = {}): Promise<PaginatedData<User>> {
  const queryString = buildQueryString(params)
  return apiCall<PaginatedData<User>>(`/users${queryString}`)
}

export async function getUser(userId: string): Promise<User> {
  const response = await apiCall<ApiResponse<User>>(`/users?id=${userId}`)
  return response.data
}

export async function createUser(data: {
  name: string;
  email: string;
  password?: string;
  company_role: string;
  apps?: Array<{ app_id: string; role: string }>;
}): Promise<User> {
  const response = await apiCall<ApiResponse<User>>('/users', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  return response.data
}

export async function updateUser(userId: string, data: Partial<User>): Promise<User> {
  const response = await apiCall<ApiResponse<User>>(`/users?id=${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  return response.data
}

export async function deleteUser(userId: string): Promise<void> {
  await apiCall(`/users?id=${userId}`, {
    method: 'DELETE',
  })
}

// === USER APP MANAGEMENT ===

export async function addUserToApp(appId: string, userId: string, role: string): Promise<void> {
  await apiCall('/app/users', {
    method: 'POST',
    body: JSON.stringify({ 
      app_id: appId,
      user_id: userId,
      role 
    }),
  })
}

export async function updateUserAppRole(appId: string, userId: string, role: string): Promise<void> {
  await apiCall(`/app/users?id=${appId}&user_id=${userId}`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  })
}

export async function removeUserFromApp(appId: string, userId: string): Promise<void> {
  await apiCall(`/app/users?id=${appId}&user_id=${userId}`, {
    method: 'DELETE',
  })
}

export async function getUserQueues(_userId: string, appId: string): Promise<Queue[]> {
  // Get all queues for the app and filter by user assignment
  await getQueues({ app_id: appId })
  // For now, return empty array as we need a specific endpoint for user's queues
  // TODO: Implement /user/queues endpoint in API
  return []
}

// === QUEUES API ===

export async function getQueues(params: { 
  app_id?: string;
  is_active?: boolean;
  page?: number;
  limit?: number;
} = {}): Promise<PaginatedData<Queue>> {
  const queryString = buildQueryString(params)
  return apiCall<PaginatedData<Queue>>(`/queues${queryString}`)
}

export async function getQueue(queueId: string): Promise<Queue> {
  const response = await apiCall<ApiResponse<Queue>>(`/queues?id=${queueId}`)
  return response.data
}

export async function createQueue(data: {
  app_id: string;
  name: string;
  description?: string;
  is_private?: boolean;
}): Promise<Queue> {
  // Map 'name' to 'queue_name' for API
  const apiData = {
    app_id: data.app_id,
    queue_name: data.name,
    description: data.description,
    is_private: data.is_private,
  }
  const response = await apiCall<ApiResponse<Queue>>('/queues', {
    method: 'POST',
    body: JSON.stringify(apiData),
  })
  return response.data
}

export async function updateQueue(queueId: string, data: Partial<Queue>): Promise<Queue> {
  // Map 'name' to 'queue_name' if present
  const apiData: any = { ...data }
  if ('name' in data) {
    apiData.queue_name = data.name
    delete apiData.name
  }
  const response = await apiCall<ApiResponse<Queue>>(`/queues?id=${queueId}`, {
    method: 'PUT',
    body: JSON.stringify(apiData),
  })
  return response.data
}

export async function deleteQueue(queueId: string): Promise<void> {
  await apiCall(`/queues?id=${queueId}`, {
    method: 'DELETE',
  })
}

// === QUEUE USERS API ===

export interface QueueUser {
  user_id: string
  name: string
  email: string
  role: 'agent' | 'manager'
  assigned_ts: string
}

export async function getQueueUsers(queueId: string): Promise<QueueUser[]> {
  const response = await apiCall<ApiResponse<QueueUser[]>>(`/queue/users?id=${queueId}`)
  return response.data
}

export async function addUserToQueue(data: {
  queue_id: string
  user_id: string
  role: 'agent' | 'manager'
}): Promise<{ message: string }> {
  const response = await apiCall<ApiResponse<{ message: string }>>('/queue/users', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  return response.data
}

export async function removeUserFromQueue(queueId: string, userId: string): Promise<void> {
  await apiCall(`/queue/users?id=${queueId}&user_id=${userId}`, {
    method: 'DELETE',
  })
}

export async function addQueueUser(queueId: string, userId: string, role: 'agent' | 'manager'): Promise<void> {
  await apiCall(`/queue/users`, {
    method: 'POST',
    body: JSON.stringify({ queue_id: queueId, user_id: userId, role }),
  })
}

export async function updateQueueUser(queueId: string, userId: string, role: 'agent' | 'manager'): Promise<{ message: string }> {
  const response = await apiCall<ApiResponse<{ message: string }>>(`/queue/users?id=${queueId}&user_id=${userId}`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  })
  return response.data
}

// Alias for backward compatibility
export const updateQueueUserRole = updateQueueUser

// === AUTO RESPONDERS API ===

export interface AutoResponder {
  auto_responder_id: string
  app_id: string
  delay_seconds: number
  message: string
  channels: string[]
  enabled: boolean
  order_index: number
  created_ts: string
  updated_ts: string
}

export async function getAutoResponders(appId: string): Promise<AutoResponder[]> {
  const response = await apiCall<ApiResponse<AutoResponder[]>>(`/auto-responders?app_id=${appId}`)
  return response.data
}

export async function getAutoResponder(id: string): Promise<AutoResponder> {
  const response = await apiCall<ApiResponse<AutoResponder>>(`/auto-responders?id=${id}`)
  return response.data
}

export async function createAutoResponder(data: {
  app_id: string
  delay_seconds: number
  message: string
  channels: string[]
  enabled: boolean
}): Promise<{ auto_responder_id: string; message: string }> {
  const response = await apiCall<ApiResponse<{ auto_responder_id: string; message: string }>>('/auto-responders', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  return response.data
}

export async function updateAutoResponder(id: string, data: Partial<AutoResponder>): Promise<{ auto_responder_id: string; message: string }> {
  const response = await apiCall<ApiResponse<{ auto_responder_id: string; message: string }>>(`/auto-responders?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  return response.data
}

export async function deleteAutoResponder(id: string): Promise<{ message: string }> {
  const response = await apiCall<ApiResponse<{ message: string }>>(`/auto-responders?id=${id}`, {
    method: 'DELETE',
  })
  return response.data
}

export async function reorderAutoResponders(appId: string): Promise<{ message: string; count: number }> {
  const response = await apiCall<ApiResponse<{ message: string; count: number }>>('/auto-responder/reorder', {
    method: 'POST',
    body: JSON.stringify({ app_id: appId }),
  })
  return response.data
}

// === INTEGRATIONS API ===

export interface Integration {
  integration_id: string
  app_id: string
  integration_type: 'email' | 'sms' | 'web' | 'facebook' | 'twitter' | 'whatsapp' | 'slack' | 'telegram' | 'custom'
  integration_name: string
  description?: string
  status: 'active' | 'inactive' | 'error'
  config: Record<string, any>
  secrets?: string // Encrypted
  webhook_url?: string
  last_activity_ts?: string
  error_message?: string
  error_ts?: string
  created_by?: {
    user_id: string
    name?: string
  }
  created_ts: string
  updated_ts: string
}

export async function getIntegrations(appId: string, companyIdOverride?: string): Promise<Integration[]> {
  const headers: Record<string, string> = {}
  if (companyIdOverride) {
    headers['X-Company-ID'] = companyIdOverride
  }
  const response = await apiCall<ApiResponse<Integration[]>>(`/integrations?app_id=${appId}`, {
    headers
  })
  return response.data
}

export async function createIntegration(data: {
  app_id: string
  integration_type: string
  integration_name: string
  description?: string
  status?: string
  config?: Record<string, any>
  secrets?: Record<string, any>
  webhook_url?: string
}): Promise<{ integration_id: string; message: string }> {
  const response = await apiCall<ApiResponse<{ integration_id: string; message: string }>>('/integrations', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  return response.data
}

export async function updateIntegration(integrationId: string, data: Partial<Integration>): Promise<{ integration_id: string; message: string }> {
  const response = await apiCall<ApiResponse<{ integration_id: string; message: string }>>(`/integrations?id=${integrationId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  return response.data
}

export async function deleteIntegration(integrationId: string): Promise<void> {
  await apiCall(`/integrations?id=${integrationId}`, {
    method: 'DELETE',
  })
}

export async function testIntegration(integrationId: string): Promise<{ success: boolean; message: string; details?: any }> {
  const response = await apiCall<ApiResponse<{ success: boolean; message: string; details?: any }>>('/integration/test', {
    method: 'POST',
    body: JSON.stringify({ integration_id: integrationId }),
  })
  return response.data
}

export interface IntegrationWebhookInfo {
  integration_id: string
  integration_name: string
  integration_type: string
  app_name: string
  status: string
  webhook?: {
    url: string
    method: string
    configuration_steps: string[]
    expected_headers: Record<string, string>
    expected_parameters: Record<string, string>
  }
  recent_activity?: Array<{
    type: string
    message: string
    timestamp: string
  }>
  webhook_stats?: {
    messages_received_24h: number
    last_activity: string | null
  }
}

export async function getIntegrationWebhookInfo(integrationId: string): Promise<IntegrationWebhookInfo> {
  const response = await apiCall<ApiResponse<IntegrationWebhookInfo>>(`/integration/webhook-info?id=${integrationId}`)
  return response.data
}

// Initialize on load
if (typeof window !== 'undefined') {
  loadAuthToken()
  loadCompanyId()
}

// Reporting & Analytics

export interface StatsCalculationRequest {
  date?: string // YYYY-MM-DD format
  incremental?: boolean
}

export interface StatsCalculationResponse {
  date: string
  mode: "incremental" | "full"
  success: boolean
  elapsed_time: string
  agent_stats: {
    agents_processed: number
    errors: number
  }
  app_stats: {
    apps_processed: number
    errors: number
  }
  error?: string
}

export async function calculateDailyStatistics(data: StatsCalculationRequest = {}): Promise<StatsCalculationResponse> {
  // Create a custom fetch that excludes the company ID header
  const response = await fetch(`${API_BASE_URL}/reporting/calculate-stats`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${getAuthToken()}`,
      // Explicitly not including X-Company-ID header
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error?.message || "Failed to calculate statistics")
  }

  const result = await response.json()
  return result.data
}

// === REPORTING ENDPOINTS ===

export async function getReportingOverview(params: { days?: number } = {}): Promise<ReportingOverview> {
  const queryString = buildQueryString(params)
  const response = await apiCall<ApiResponse<ReportingOverview>>(`/reporting/overview${queryString}`)
  return response.data
}

export async function getReportingCharts(params: { days?: number } = {}): Promise<ReportingCharts> {
  const queryString = buildQueryString(params)
  const response = await apiCall<ApiResponse<ReportingCharts>>(`/reporting/charts${queryString}`)
  return response.data
}

export async function getReportingAgentPerformance(params: { 
  days?: number
  app_id?: string 
} = {}): Promise<ReportingAgentPerformance> {
  const queryString = buildQueryString(params)
  const response = await apiCall<ApiResponse<ReportingAgentPerformance>>(`/reporting/agent-performance${queryString}`)
  return response.data
}

export async function exportReportData(params: {
  type: 'overview' | 'agent-performance' | 'all'
  days?: number
  app_id?: string
}): Promise<Blob> {
  const queryString = buildQueryString(params)
  
  const response = await fetch(`${API_BASE_URL}/reporting/export${queryString}`, {
    method: 'GET',
    headers: getHeaders(),
  })
  
  if (!response.ok) {
    let errorData: any = {}
    try {
      errorData = await response.json()
    } catch (e) {
      errorData = { error: { message: `HTTP ${response.status}: ${response.statusText}` } }
    }
    
    const errorMessage = errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`
    throw new Error(errorMessage)
  }
  
  return response.blob()
}

export function downloadReportCSV(blob: Blob, type: string) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `messagelink_report_${type}_${new Date().toISOString().split('T')[0]}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

// === PLATFORM ADMIN API ===

export async function getAdminCompanies(filters: {
  status?: string
  search?: string
  page?: number
  limit?: number
  sort?: string
  order?: string
} = {}): Promise<PaginatedData<PlatformCompany>> {
  const queryString = buildQueryString({
    ...filters,
    limit: filters.limit || 50
  })
  return apiCall<PaginatedData<PlatformCompany>>(`/admin/companies${queryString}`, {
    skipCompanyHeader: true
  })
}

export async function getAdminCompany(companyId: string): Promise<PlatformCompany> {
  const response = await apiCall<ApiResponse<PlatformCompany>>(`/admin/companies?id=${companyId}`, {
    skipCompanyHeader: true
  })
  return response.data
}

export async function getAdminUser(userId: string): Promise<PlatformUser> {
  const queryString = buildQueryString({ id: userId })
  const response = await apiCall<ApiResponse<PlatformUser>>(`/admin/user${queryString}`, {
    skipCompanyHeader: true
  })
  return response.data
}

export async function getUserActivities(filters: {
  user_id?: string
  subject_type?: string
  subject_id?: string
  action?: string
  page?: number
  limit?: number
  sort?: string
  order?: string
} = {}): Promise<PaginatedData<Activity>> {
  const queryString = buildQueryString(filters)
  return apiCall<PaginatedData<Activity>>(`/activities${queryString}`)
}

export async function createAdminCompany(data: CreateCompanyRequest): Promise<{ company_id: string; admin_user_id?: string; message: string }> {
  return apiCall<{ company_id: string; admin_user_id?: string; message: string }>('/admin/companies', {
    method: 'POST',
    body: JSON.stringify(data),
    skipCompanyHeader: true
  })
}

export async function updateAdminCompany(companyId: string, data: UpdateCompanyRequest): Promise<{ company_id: string; message: string }> {
  return apiCall<{ company_id: string; message: string }>(`/admin/companies?id=${companyId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
    skipCompanyHeader: true
  })
}

export async function deleteAdminCompany(companyId: string): Promise<void> {
  await apiCall(`/admin/companies?id=${companyId}`, {
    method: 'DELETE',
    skipCompanyHeader: true
  })
}

export async function getAdminApps(filters: {
  company_id?: string
  status?: string
  page?: number
  limit?: number
}): Promise<PaginatedData<App>> {
  const query = buildQueryString(filters)
  return apiCall<PaginatedData<App>>(`/admin/apps${query}`, {
    skipCompanyHeader: true
  })
}

export async function getAdminApp(appId: string): Promise<App> {
  return apiCall<ApiResponse<App>>(`/admin/apps?id=${appId}`, {
    skipCompanyHeader: true
  }).then(r => r.data)
}

export async function createAdminApp(data: { 
  company_id: string;
  app_name: string;
  description?: string;
  logo_url?: string;
}): Promise<App> {
  const response = await apiCall<ApiResponse<App>>('/admin/apps', {
    method: 'POST',
    body: JSON.stringify(data),
    skipCompanyHeader: true
  })
  return response.data
}

export async function updateAdminApp(appId: string, data: Partial<App>): Promise<App> {
  const response = await apiCall<ApiResponse<App>>(`/admin/apps?id=${appId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
    skipCompanyHeader: true
  })
  return response.data
}

export async function deleteAdminApp(appId: string): Promise<void> {
  await apiCall(`/admin/apps?id=${appId}`, {
    method: 'DELETE',
    skipCompanyHeader: true
  })
}

export async function getAdminUsers(filters: {
  company_id?: string
  status?: string
  is_platform_admin?: string
  search?: string
  page?: number
  limit?: number
  sort?: string
  order?: string
} = {}): Promise<PaginatedData<PlatformUser>> {
  const queryString = buildQueryString({
    ...filters,
    limit: filters.limit || 50
  })
  return apiCall<PaginatedData<PlatformUser>>(`/admin/users${queryString}`, {
    skipCompanyHeader: true
  })
}

export async function createAdminUser(data: {
  name: string;
  email: string;
  timezone?: string;
  company_id?: string;
  company_role?: string;
}): Promise<{ user_id: string; message: string; email_sent: boolean }> {
  return apiCall<{ user_id: string; message: string; email_sent: boolean }>('/admin/users', {
    method: 'POST',
    body: JSON.stringify(data),
    skipCompanyHeader: true
  })
}

export async function createAdminQueue(data: {
  app_id: string;
  name: string;
  description?: string;
  is_private?: boolean;
  is_active?: boolean;
}): Promise<Queue> {
  // Map 'name' to 'queue_name' for API
  const apiData = {
    app_id: data.app_id,
    queue_name: data.name,
    description: data.description,
    is_private: data.is_private,
    is_active: data.is_active,
  }
  const response = await apiCall<ApiResponse<Queue>>('/admin/queues', {
    method: 'POST',
    body: JSON.stringify(apiData),
    skipCompanyHeader: true
  })
  return response.data
}

export async function updateAdminQueue(queueId: string, data: Partial<Queue>): Promise<Queue> {
  // Map 'name' to 'queue_name' if present
  const apiData: any = { ...data }
  if ('name' in data) {
    apiData.queue_name = data.name
    delete apiData.name
  }
  const response = await apiCall<ApiResponse<Queue>>(`/admin/queues?id=${queueId}`, {
    method: 'PUT',
    body: JSON.stringify(apiData),
    skipCompanyHeader: true
  })
  return response.data
}

export async function deleteAdminQueue(queueId: string): Promise<void> {
  await apiCall(`/admin/queues?id=${queueId}`, {
    method: 'DELETE',
    skipCompanyHeader: true
  })
}

// Platform Admin - Company Status Management
export async function updateAdminCompanyStatus(companyId: string, status: string, reason?: string): Promise<void> {
  await apiCall('/admin/company/status', {
    method: 'POST',
    body: JSON.stringify({ company_id: companyId, status, reason }),
    skipCompanyHeader: true
  })
}

// Platform Admin - App Status Management
export async function updateAdminAppStatus(appId: string, status: string, reason?: string): Promise<void> {
  await apiCall('/admin/app/status', {
    method: 'POST',
    body: JSON.stringify({ app_id: appId, status, reason }),
    skipCompanyHeader: true
  })
}

// Platform Admin - Get Queues for App
export async function getAdminQueues(filters: {
  app_id?: string
  is_active?: boolean
  is_private?: boolean
  search?: string
  page?: number
  limit?: number
} = {}): Promise<PaginatedData<Queue>> {
  const queryString = buildQueryString(filters)
  return apiCall<PaginatedData<Queue>>(`/admin/queues${queryString}`, {
    skipCompanyHeader: true
  })
}

// Platform Admin - User Company Management
export async function addAdminUserToCompany(userId: string, companyId: string, role: string): Promise<void> {
  await apiCall('/admin/user-company', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, company_id: companyId, company_role: role }),
    skipCompanyHeader: true
  })
}

export async function updateAdminUserCompanyRole(userId: string, companyId: string, role: string): Promise<void> {
  await apiCall('/admin/user-company', {
    method: 'PUT',
    body: JSON.stringify({ user_id: userId, company_id: companyId, company_role: role }),
    skipCompanyHeader: true
  })
}

export async function removeAdminUserFromCompany(userId: string, companyId: string): Promise<void> {
  await apiCall('/admin/user-company', {
    method: 'DELETE',
    body: JSON.stringify({ user_id: userId, company_id: companyId }),
    skipCompanyHeader: true
  })
}

// Platform Admin - User App Management
export async function addAdminUserToApp(userId: string, appId: string, role: string, companyId: string): Promise<void> {
  await apiCall('/admin/user-app', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, app_id: appId, app_role: role, company_id: companyId }),
    skipCompanyHeader: true
  })
}

export async function updateAdminUserAppRole(userId: string, appId: string, role: string, companyId: string): Promise<void> {
  await apiCall('/admin/user-app', {
    method: 'PUT',
    body: JSON.stringify({ user_id: userId, app_id: appId, app_role: role, company_id: companyId }),
    skipCompanyHeader: true
  })
}

export async function removeAdminUserFromApp(userId: string, appId: string, companyId: string): Promise<void> {
  await apiCall('/admin/user-app', {
    method: 'DELETE',
    body: JSON.stringify({ user_id: userId, app_id: appId, company_id: companyId }),
    skipCompanyHeader: true
  })
}

// Platform Admin - User Queue Management
export async function addAdminUserToQueue(userId: string, queueId: string, role: string): Promise<void> {
  await apiCall('/admin/user-queue', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, queue_id: queueId, queue_role: role }),
    skipCompanyHeader: true
  })
}

export async function updateAdminUserQueueRole(userId: string, queueId: string, role: string): Promise<void> {
  await apiCall('/admin/user-queue', {
    method: 'PUT',
    body: JSON.stringify({ user_id: userId, queue_id: queueId, queue_role: role }),
    skipCompanyHeader: true
  })
}

export async function removeAdminUserFromQueue(userId: string, queueId: string): Promise<void> {
  await apiCall('/admin/user-queue', {
    method: 'DELETE',
    body: JSON.stringify({ user_id: userId, queue_id: queueId }),
    skipCompanyHeader: true
  })
}

// Platform admin user management functions
export async function suspendAdminUser(userId: string): Promise<void> {
  await apiCall(`/admin/user/suspend?id=${userId}`, {
    method: 'POST',
    skipCompanyHeader: true
  })
}

export async function activateAdminUser(userId: string): Promise<void> {
  await apiCall(`/admin/user/activate?id=${userId}`, {
    method: 'POST',
    skipCompanyHeader: true
  })
}

export async function makePlatformAdmin(userId: string): Promise<void> {
  await apiCall(`/admin/user/make-platform-admin?id=${userId}`, {
    method: 'POST',
    skipCompanyHeader: true
  })
}

export async function removePlatformAdmin(userId: string): Promise<void> {
  await apiCall(`/admin/user/remove-platform-admin?id=${userId}`, {
    method: 'POST',
    skipCompanyHeader: true
  })
}

export async function deleteAdminUser(userId: string): Promise<void> {
  await apiCall(`/admin/users?id=${userId}`, {
    method: 'DELETE',
    skipCompanyHeader: true
  })
}

// === API KEY MANAGEMENT ===
// Note: API keys are user-owned, not company-owned, so no company context is needed

export async function getApiKeys(filters: { page?: number; limit?: number; status?: string } = {}): Promise<ApiKeyListResponse> {
  const queryString = buildQueryString(filters)
  const response = await apiCall<ApiResponse<ApiKey[]>>(`/api-keys/list${queryString}`, {
    skipCompanyHeader: true
  })
  // The API returns the array directly in data, but we need to wrap it in the expected structure
  return {
    api_keys: response.data,
    pagination: {
      page: filters.page || 1,
      limit: filters.limit || 100,
      total: response.data.length,
      pages: 1
    }
  }
}

export async function getApiKey(apiKeyId: string): Promise<ApiResponse<ApiKey>> {
  return apiCall<ApiResponse<ApiKey>>(`/api-keys/get?id=${apiKeyId}`, {
    skipCompanyHeader: true
  })
}

export async function createApiKey(data: CreateApiKeyRequest): Promise<ApiResponse<{ api_key_id: string; api_key: string; name: string; message: string }>> {
  return apiCall<ApiResponse<{ api_key_id: string; api_key: string; name: string; message: string }>>('/api-keys/create', {
    method: 'POST',
    body: JSON.stringify(data),
    skipCompanyHeader: true
  })
}

export async function updateApiKey(apiKeyId: string, data: UpdateApiKeyRequest): Promise<ApiResponse<{ api_key_id: string; message: string }>> {
  return apiCall<ApiResponse<{ api_key_id: string; message: string }>>(`/api-keys/update?id=${apiKeyId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
    skipCompanyHeader: true
  })
}

export async function deleteApiKey(apiKeyId: string): Promise<ApiResponse<{ message: string }>> {
  return apiCall<ApiResponse<{ message: string }>>(`/api-keys/delete?id=${apiKeyId}`, {
    method: 'DELETE',
    skipCompanyHeader: true
  })
}

export async function grantApiKeyPermission(apiKeyId: string, integrationId: string): Promise<ApiResponse<{ permission_id: string; message: string }>> {
  return apiCall<ApiResponse<{ permission_id: string; message: string }>>('/api-keys/permissions/grant', {
    method: 'POST',
    body: JSON.stringify({ api_key_id: apiKeyId, integration_id: integrationId }),
    skipCompanyHeader: true
  })
}

export async function revokeApiKeyPermission(permissionId: string): Promise<ApiResponse<{ permission_id: string; message: string }>> {
  return apiCall<ApiResponse<{ permission_id: string; message: string }>>(`/api-keys/permissions/revoke?permission_id=${permissionId}`, {
    method: 'DELETE',
    skipCompanyHeader: true
  })
}

// Get all integrations across all companies/apps for the current user using the new endpoint
export async function getApiKeyIntegrations(params?: {
  status?: string
  type?: string
  search?: string
}): Promise<{
  grouped: Array<{
    company_id: string
    company_name: string
    apps: Array<{
      app_id: string
      app_name: string
      user_role: string
      integrations: Array<{
        integration_id: string
        type: string
        name: string
        description?: string
        status: string
        last_activity_ts?: string
        error_message?: string
        created_ts: string
        updated_ts: string
      }>
    }>
  }>
  integrations: Array<Integration & { app_name: string; company_name: string }>
  stats: {
    total_integrations: number
    total_companies: number
    total_apps: number
    by_status: Record<string, number>
    by_type: Record<string, number>
  }
  is_platform_admin: boolean
}> {
  const queryString = buildQueryString(params || {})
  const response = await apiCall<ApiResponse<any>>(`/api-keys/integrations${queryString}`)
  return response.data
}

// Legacy function - now uses the new endpoint
export async function getAllUserIntegrations(): Promise<Integration[]> {
  try {
    const response = await getApiKeyIntegrations()
    // Return the flat list with company and app context
    return response.integrations
  } catch (error) {
    console.error('Failed to fetch user integrations:', error)
    return []
  }
}

// ============= BILLING API ENDPOINTS =============

// IMPORTANT: Invoice list endpoint returns nested structure per billing_api.md
export async function getInvoices(params: {
  page?: number
  limit?: number
  status?: string
  start_date?: string
  end_date?: string
} = {}): Promise<ApiResponse<{
  invoices: Invoice[]
  statistics: InvoiceStatistics
}>> {
  const queryString = buildQueryString(params)
  // Returns nested structure with data.invoices and data.statistics
  return apiCall<ApiResponse<{
    invoices: Invoice[]
    statistics: InvoiceStatistics
  }>>(`/billing/invoices${queryString}`)
}

// Get detailed invoice information
export async function getInvoiceDetail(invoiceId: string): Promise<ApiResponse<InvoiceDetail>> {
  return apiCall<ApiResponse<InvoiceDetail>>(`/billing/invoices?id=${invoiceId}`)
}

// Get current usage with subscription, credits, and payment status (consolidated)
export async function getCurrentUsage(): Promise<ApiResponse<CurrentUsage>> {
  return apiCall<ApiResponse<CurrentUsage>>('/billing/usage/current')
}

// Get usage history with proper type
export async function getUsageHistory(params: {
  start_date?: string
  end_date?: string
  limit?: number
} = {}): Promise<ApiResponse<UsageHistory>> {
  const queryString = buildQueryString(params)
  return apiCall<ApiResponse<UsageHistory>>(`/billing/usage/history${queryString}`)
}

// Get payment method information
export async function getPaymentMethodInfo(): Promise<ApiResponse<PaymentMethodInfo>> {
  return apiCall<ApiResponse<PaymentMethodInfo>>('/billing/payment-method')
}

// Open Stripe Customer Portal for payment management
export async function openBillingPortal(): Promise<ApiResponse<PortalSessionResponse>> {
  const response = await apiCall<ApiResponse<PortalSessionResponse>>('/billing/portal', {
    method: 'POST',
    body: JSON.stringify({
      return_url: `${window.location.origin}/admin?tab=billing`
    })
  })
  
  // Redirect to Stripe portal
  if (response.data.portal_url) {
    window.location.href = response.data.portal_url
  }
  
  return response
}

// Get Stripe customer with proper type (✅ BACKEND COMPLETED)
// Backend: /api/v1/api/v1/billing/stripe-customer.php
export async function getStripeCustomer(): Promise<ApiResponse<StripeCustomerInfo>> {
  return apiCall<ApiResponse<StripeCustomerInfo>>('/billing/stripe-customer')
}

// Creates Stripe customer and returns portal URL for immediate setup
export async function createStripeCustomer(billingUserId: string): Promise<ApiResponse<CreateStripeCustomerResponse>> {
  return apiCall<ApiResponse<CreateStripeCustomerResponse>>('/billing/stripe-customer', {
    method: 'POST',
    body: JSON.stringify({
      billing_user_id: billingUserId
    })
  })
}

// Update credit block size (admin only)
export async function updateCreditBlockSize(blockSizeDollars: number): Promise<ApiResponse<any>> {
  return apiCall<ApiResponse<any>>('/billing/credit-block-size', {
    method: 'PUT',
    body: JSON.stringify({
      block_size_dollars: blockSizeDollars
    })
  })
}

// Initialize subscription (admin only)
export async function initializeSubscription(billingUserId: string, startDate?: string): Promise<ApiResponse<any>> {
  return apiCall<ApiResponse<any>>('/billing/subscribe', {
    method: 'POST',
    body: JSON.stringify({
      billing_user_id: billingUserId,
      start_date: startDate
    })
  })
}

// Retry failed payment (admin only)
export async function retryPayment(invoiceId: string): Promise<ApiResponse<any>> {
  return apiCall<ApiResponse<any>>('/billing/retry-payment', {
    method: 'POST',
    body: JSON.stringify({
      invoice_id: invoiceId
    })
  })
}

// Charge invoice manually (admin only)
export async function chargeInvoice(invoiceId: string): Promise<ApiResponse<any>> {
  return apiCall<ApiResponse<any>>('/billing/charge-invoice', {
    method: 'POST',
    body: JSON.stringify({
      invoice_id: invoiceId
    })
  })
}
